import {
  DEFAULT_EMPTY_STATE,
  EMPTY_STATE_BY_PERSONA,
  type DiligencePackage,
} from '../../lib/diligencePackages';

const ACCENT_CLASSES: Record<DiligencePackage['accent'], { ring: string; bg: string; text: string; hover: string }> = {
  wb:      { ring: 'ring-wb-200',      bg: 'bg-wb-50',      text: 'text-wb-800',      hover: 'group-hover:bg-wb-100' },
  emerald: { ring: 'ring-emerald-200', bg: 'bg-emerald-50', text: 'text-emerald-800', hover: 'group-hover:bg-emerald-100' },
  amber:   { ring: 'ring-amber-200',   bg: 'bg-amber-50',   text: 'text-amber-800',   hover: 'group-hover:bg-amber-100' },
  rose:    { ring: 'ring-rose-200',    bg: 'bg-rose-50',    text: 'text-rose-800',    hover: 'group-hover:bg-rose-100' },
  violet:  { ring: 'ring-violet-200',  bg: 'bg-violet-50',  text: 'text-violet-800',  hover: 'group-hover:bg-violet-100' },
  teal:    { ring: 'ring-teal-200',    bg: 'bg-teal-50',    text: 'text-teal-800',    hover: 'group-hover:bg-teal-100' },
  sky:     { ring: 'ring-sky-200',     bg: 'bg-sky-50',     text: 'text-sky-800',     hover: 'group-hover:bg-sky-100' },
};

/**
 * First-message empty state. Shown when a conversation has no messages.
 * Spec §0.3: "no empty states that punish curiosity" — every dead end suggests
 * a path forward.
 *
 * Persona-aware: the diligence packages, single-question suggestions, headline
 * and "connected sources" description all come from the persona-specific
 * config in `lib/diligencePackages.ts`. Each executive gets a different curated
 * starting set so the user lands on questions actually relevant to their domain.
 *
 * Audience framing: this is read by Walker Blue executives AND the acquirer's
 * diligence team. Tone is welcoming-but-professional, not playful-chatbot.
 */
export function EmptyState({
  onPick,
  personaId,
}: {
  onPick: (text: string) => void;
  personaId?: string;
}) {
  // Look up the persona's content; fall back to the CFO config for
  // unknown / coming-soon personas so the page always renders something.
  const content =
    (personaId ? EMPTY_STATE_BY_PERSONA[personaId] : undefined) ??
    DEFAULT_EMPTY_STATE;

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-10 text-center sm:py-16">
      <img
        src="/walker-blue-logo.png"
        alt=""
        className="mb-5 h-14 w-auto"
        aria-hidden="true"
      />
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
        Intelligence Hub
      </div>
      {/* Editorial serif headline — same display family as the HomePage
          greeting, so a fresh conversation reads as the natural next page
          of the same story rather than a different surface. */}
      <h2 className="display-heading mt-3 max-w-2xl text-[32px] sm:text-[42px]">
        {content.headline}
      </h2>
      <p className="mt-4 max-w-lg text-[14.5px] leading-relaxed text-slate-600">
        {content.description} Every answer cites its sources; every chart is exportable.
      </p>

      {/* Diligence packages — pre-curated tracks for this persona's domain.
          Click on a package fires its first question; the rest follow
          one click at a time so users can drill at their own pace. */}
      <div className="mt-10 w-full text-left">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="section-label">Diligence packages</h3>
          <span className="text-[11px] text-slate-400">Curated diligence tracks</span>
        </div>
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          {content.packages.map((pkg) => {
            const a = ACCENT_CLASSES[pkg.accent];
            return (
              <button
                key={pkg.id}
                type="button"
                onClick={() => onPick(pkg.questions[0]!)}
                className="group flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-4 text-left transition-all hover:border-slate-300 hover:shadow-sm"
                title={`Asks: "${pkg.questions[0]}"`}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex h-6 items-center rounded-md px-2 text-[10px] font-semibold uppercase tracking-[0.12em] ring-1 ${a.bg} ${a.ring} ${a.text}`}
                  >
                    {pkg.title}
                  </span>
                  <span className="text-[10px] text-slate-400">
                    {pkg.questions.length} questions
                  </span>
                </div>
                <p className="text-sm leading-snug text-slate-700 group-hover:text-slate-900">
                  {pkg.description}
                </p>
                <span className="mt-auto inline-flex items-center gap-1 pt-1 text-[11px] font-medium text-wb-700">
                  Start with the first question →
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-10 w-full text-left">
        <div className="mb-3">
          <h3 className="section-label">Or ask a single question</h3>
        </div>
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          {content.suggestions.map((s) => (
            <button
              key={s.text}
              type="button"
              onClick={() => onPick(s.text)}
              className="group flex flex-col gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-left text-sm text-slate-700 transition-all hover:border-wb-300 hover:bg-wb-50/40 hover:shadow-sm"
            >
              <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-wb-700">
                {s.category}
              </span>
              <span className="leading-snug text-slate-800 group-hover:text-slate-900">
                {s.text}
              </span>
            </button>
          ))}
        </div>
      </div>

      <p className="mt-8 text-[11px] uppercase tracking-[0.12em] text-slate-400">
        Read-only access · all queries are audit-logged
      </p>
    </div>
  );
}
